/**
 * @fileoverview Mapa da área de Configurações: grupos, seções, campos e quem vê
 * cada um. Módulo puro (sem React nem API): o menu, as rotas e os testes leem
 * daqui. Os textos seguem a convenção settings.groups.<grupo> e
 * settings.sections.<seção>.(title|description), com os campos em
 * settings.sections.<seção>.fields.<campo>.
 *
 * A busca procura no título da seção, no nome do grupo e nas palavras-chave
 * (settings.sections.<seção>.keywords e ….fields.<campo>.keywords).
 *
 * Quem vê espelha o servidor, mas não o substitui: o backend continua recusando
 * o que o perfil não pode gravar. "Admin" é profile === "admin", exatamente o
 * que o PUT /settings/:key confere. Com a regra nova de acesso ligada, cada
 * seção pede a permissão dela (ctx.can, o do usePermission), e a regra de hoje
 * é o "permitido antes".
 */

import {
  BellRing,
  Blocks,
  Building2,
  ChartPie,
  Clock,
  Gauge,
  IdCard,
  KeyRound,
  MessagesSquare,
  PhoneOff,
  ReceiptText,
  ShieldCheck,
  ShieldUser,
  Zap,
} from "lucide-react";

import { isInternalOnly } from "../../utils/accessScope";
import stripAccents from "../../utils/stripAccents";

export const GROUPS = ["account", "service", "system", "company"];

// Seção aberta por /settings no computador, quando o perfil pode vê-la.
export const LANDING = "schedules";

// Link do onboarding até 15/10/2026 (/settings#horarios): favoritos e telas
// abertas antes do deploy.
const LEGACY_HASHES = { "#horarios": "schedules" };

const isServiceAdmin = (ctx) => ctx.can("settings.manage", !ctx.internal && ctx.isAdmin);
const isCompanyManager = (key) => (ctx) => ctx.can(key, !ctx.internal && (ctx.isAdmin || ctx.isSuper));

export const ITEMS = [
  { id: "security", group: "account", icon: ShieldCheck },
  {
    id: "schedules",
    group: "service",
    icon: Clock,
    // A semana e os feriados valem sempre no atendimento (feriado → horário da
    // conexão → semana da empresa); o servidor só aceita a gravação de admin ou super.
    canView: isCompanyManager("schedules.manage"),
    fields: [{ id: "weeklyHours" }, { id: "exceptions" }],
  },
  {
    // Quem vê as conversas dos atendentes (o servidor confere: ticketScope). O
    // Supervisor só existe com a regra nova ligada: sem ela, a opção dele não
    // aparece; com ela, pede também team.manage_access, como o servidor.
    id: "conversations",
    group: "service",
    icon: MessagesSquare,
    canView: isServiceAdmin,
    fields: [
      { id: "ticketVisibility" },
      {
        id: "supervisorTicketVisibility",
        canView: (ctx) => ctx.can("settings.manage", false) && ctx.can("team.manage_access", false),
      },
    ],
  },
  {
    id: "quick-replies",
    group: "service",
    icon: Zap,
    canView: isServiceAdmin,
    fields: [{ id: "quickMessagesScope" }],
  },
  {
    id: "indicators",
    group: "service",
    icon: Gauge,
    canView: isServiceAdmin,
    fields: [
      { id: "noResponseIndicator" },
      { id: "unrepliedClockIcon" },
      { id: "noResponseBusinessHours" },
      { id: "artThresholds" },
      { id: "shortReplyFilter" },
      { id: "shortReplyWords" },
      { id: "shortReplyStickers" },
      { id: "shortReplyAutoClose" },
    ],
  },
  {
    id: "calls",
    group: "service",
    icon: PhoneOff,
    canView: isServiceAdmin,
    fields: [{ id: "callRejection" }],
  },
  {
    id: "notifications",
    group: "system",
    icon: BellRing,
    canView: isServiceAdmin,
    fields: [{ id: "webNotifications" }, { id: "internalChatNotifications" }, { id: "smsAlert" }],
  },
  {
    id: "modules",
    group: "system",
    icon: Blocks,
    canView: (ctx) => isServiceAdmin(ctx) || (!ctx.internal && ctx.canEditSuperSettings),
    fields: [
      { id: "enableAcademy", canView: (ctx) => ctx.can("settings.manage", ctx.isAdmin) },
      { id: "triageEnabled", canView: (ctx) => ctx.canEditSuperSettings },
      { id: "triageRetention", canView: (ctx) => ctx.canEditSuperSettings },
      { id: "ticketCloseButton", canView: (ctx) => ctx.canEditSuperSettings },
    ],
  },
  { id: "company", group: "company", icon: Building2, canView: isCompanyManager("company.manage") },
  { id: "job-roles", group: "company", icon: IdCard, canView: isCompanyManager("team.manage") },
  // Grupos de contas da Empresa (as contas com o mesmo groupName): só quando a
  // conta faz parte de uma.
  {
    id: "departments",
    group: "company",
    icon: ChartPie,
    canView: (ctx) => ctx.empresa && isCompanyManager("departments.manage")(ctx),
  },
  // O perfil de acesso e as exceções de cada pessoa: só com a regra nova ligada
  // (sem ela não há Supervisor nem exceções, e o cadastro segue como hoje).
  { id: "team-access", group: "company", icon: KeyRound, canView: (ctx) => ctx.can("team.manage_access", false) },
  // Como nas abas antigas: a política aparece também para admin "Apenas Interno".
  {
    id: "company-security",
    group: "company",
    icon: ShieldUser,
    canView: (ctx) => ctx.can("company.security.manage", ctx.isAdmin || ctx.isSuper),
  },
  { id: "billing", group: "company", icon: ReceiptText, canView: isCompanyManager("billing.manage") },
];

const canSee = (entry, ctx) => !entry.canView || entry.canView(ctx);

/**
 * Permissões da área a partir do usuário logado. `can` é o do usePermission
 * (com a regra nova, a permissão do catálogo); sem ele, vale a regra de hoje.
 */
export const buildContext = ({ user, impersonation, can = (_key, allowedBefore) => !!allowedBefore }) => {
  const isSuper = !!user?.super;
  return {
    isAdmin: user?.profile === "admin",
    isSuper,
    internal: isInternalOnly(user),
    // A conta faz parte de uma Empresa (outras contas com o mesmo groupName).
    empresa: !!user?.company?.groupName,
    // As opções liberadas pela Sttelar (triagem, botão Resolver): o servidor
    // recusa a quem entrou "como o usuário" (impersonação).
    canEditSuperSettings: isSuper && impersonation?.mode !== "user",
    can,
  };
};

export const visibleItems = (ctx) => ITEMS.filter((item) => canSee(item, ctx));

export const visibleFields = (item, ctx) => (item.fields || []).filter((field) => canSee(field, ctx));

export const findItem = (id) => ITEMS.find((item) => item.id === id) || null;

export const sectionPath = (id) => `/settings/${id}`;

/**
 * O que a rota /settings/:section mostra:
 * - { show: id }       a seção pedida;
 * - { redirect: path } troca a URL (sem empilhar no histórico);
 * - { list: true }     /settings no celular: a lista do menu;
 * - { wait: true }     o usuário logado ainda está chegando.
 */
export const resolveRoute = ({ section, hash, ctx, ready, isMobile }) => {
  if (!section && LEGACY_HASHES[hash]) return { redirect: sectionPath(LEGACY_HASHES[hash]) };

  const visible = visibleItems(ctx);
  if (section && visible.some((item) => item.id === section)) return { show: section };
  if (!ready) return { wait: true };
  if (isMobile) return section ? { redirect: "/settings" } : { list: true };

  // "Segurança" é visível para todos, então sempre há para onde ir.
  const landing = visible.find((item) => item.id === LANDING) || visible[0];
  return { redirect: sectionPath(landing.id) };
};

// Sem acento, minúsculo e sem pontuação (as palavras-chave vêm separadas por
// vírgula: buscar "," não pode achar tudo).
const normalize = (text) =>
  stripAccents(text)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

/** Nome do campo na lista de resultados: o título próprio da busca ou o da linha. */
export const fieldLabel = (itemId, fieldId, t) =>
  t(`settings.sections.${itemId}.fields.${fieldId}.searchTitle`) ||
  t(`settings.sections.${itemId}.fields.${fieldId}.title`);

const sectionText = (item, t) =>
  [
    t(`settings.sections.${item.id}.title`),
    t(`settings.groups.${item.group}`),
    t(`settings.sections.${item.id}.keywords`),
  ].join(" ");

const fieldText = (item, field, t) =>
  [
    t(`settings.sections.${item.id}.fields.${field.id}.title`),
    fieldLabel(item.id, field.id, t),
    t(`settings.sections.${item.id}.fields.${field.id}.keywords`),
  ].join(" ");

/**
 * Busca nas seções e campos que o perfil vê, sem ligar para acentos e
 * maiúsculas: todas as palavras digitadas precisam aparecer. Um campo entra
 * como sub-resultado quando alguma palavra está nele mesmo (e o resto pode
 * estar na seção, como em "notificações sms"). `t(chave)` devolve o texto ou ""
 * se a chave não existir. Devolve [{ item, matched, fields }] na ordem do menu
 * (matched: a seção em si bateu, não só um campo), ou null quando não há busca.
 */
export const searchSettings = (query, ctx, t) => {
  const terms = normalize(query || "").split(" ").filter(Boolean);
  if (!terms.length) return null;
  const hasAll = (text) => terms.every((term) => text.includes(term));

  return visibleItems(ctx)
    .map((item) => {
      const section = normalize(sectionText(item, t));
      const fields = visibleFields(item, ctx).filter((field) => {
        const own = normalize(fieldText(item, field, t));
        return terms.some((term) => own.includes(term)) && hasAll(`${section} ${own}`);
      });
      const matched = hasAll(section);
      return matched || fields.length ? { item, matched, fields } : null;
    })
    .filter(Boolean);
};
